import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  changeMyPassword,
  deletePin,
  fetchMyProfile,
  getPinStatus,
  setPin,
  signOutOtherSessions,
  updateMyNotificationPrefs,
  updateMyPreferences,
  updateMyProfile,
  type NotificationPrefs,
  type UserPreferences,
} from '@/lib/api/profile.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useAuthStore } from '@/stores/auth.js';
import { useUserPreferences } from '@/contexts/UserPreferencesContext.js';
import { avatarColorFromName, initialsFromName } from '@/lib/theme.js';
import { formatDate, prettyLabel } from '@/lib/format.js';
import { cn } from '@/lib/utils.js';

type TabId = 'profile' | 'security' | 'notifications' | 'preferences';

const TABS: Array<{ id: TabId; label: string; hash?: string }> = [
  { id: 'profile', label: '👤 Profile' },
  { id: 'security', label: '🔐 Security', hash: 'security' },
  { id: 'notifications', label: '🔔 Notifications', hash: 'notifications' },
  { id: 'preferences', label: '⚙ Preferences' },
];

const IN_APP_LABELS: Record<string, string> = {
  ticketAssigned: 'Ticket assigned to me',
  ticketOverdue: 'Ticket becomes overdue',
  newMessage: 'New message received',
  estimateDecision: 'Estimate approved/declined',
  partsRequestUpdate: 'Parts request update',
  lowStockAlert: 'Low stock alert (manager+)',
  newInquiry: 'New inquiry received',
};

const EMAIL_LABELS: Record<string, string> = {
  weeklyPerformance: 'Weekly performance report (Monday AM)',
  dailySummary: 'Daily summary (end of day)',
  lowStockDigest: 'Low stock digest',
  negativeNps: 'Negative NPS alert (manager+)',
  negativeGoogleReview: 'New negative Google review (manager+)',
};

const SMS_LABELS: Record<string, string> = {
  urgentTicket: 'Urgent ticket alerts',
  negativeNps: 'Negative NPS score received',
  largePayment: 'Large payment recorded',
};

function passwordChecks(pw: string) {
  return {
    length: pw.length >= 8,
    number: /[0-9]/.test(pw),
    upper: /[A-Z]/.test(pw),
  };
}

export function ProfileSettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const updateSessionUser = useAuthStore((s) => s.updateUser);
  const { applyPreferencesLocally, setTheme, refreshProfile } = useUserPreferences();

  const hashTab = useMemo(() => {
    const h = location.hash.replace('#', '');
    if (h === 'security' || h === 'notifications' || h === 'preferences') return h as TabId;
    return 'profile' as TabId;
  }, [location.hash]);

  const [tab, setTab] = useState<TabId>(hashTab);

  useEffect(() => {
    setTab(hashTab);
  }, [hashTab]);

  const profileQ = useQuery({
    queryKey: ['users-me'],
    queryFn: fetchMyProfile,
  });

  const pinQ = useQuery({
    queryKey: ['users-pin-status'],
    queryFn: getPinStatus,
  });

  const profile = profileQ.data;

  // ── Profile form ──────────────────────────────────────────────────────────
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (!profile) return;
    setFirstName(profile.firstName);
    setLastName(profile.lastName);
    setEmail(profile.email);
    setPhone(profile.phone ?? '');
  }, [profile]);

  const saveProfileMut = useMutation({
    mutationFn: () =>
      updateMyProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
      }),
    onSuccess: (data) => {
      toastSuccess('Profile updated');
      updateSessionUser({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
      });
      void queryClient.invalidateQueries({ queryKey: ['users-me'] });
      void refreshProfile();
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  // ── Password ──────────────────────────────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const pwChecks = passwordChecks(newPassword);

  const changePwMut = useMutation({
    mutationFn: () => changeMyPassword({ currentPassword, newPassword }),
    onSuccess: () => {
      toastSuccess('Password changed successfully — please sign in again');
      clearAuth();
      navigate('/login');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  // ── PIN ───────────────────────────────────────────────────────────────────
  const [pinEditorOpen, setPinEditorOpen] = useState(false);
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  const setPinMut = useMutation({
    mutationFn: () =>
      setPin({
        pin: newPin,
        ...(pinQ.data?.hasPinSet ? { currentPin } : {}),
      }),
    onSuccess: () => {
      toastSuccess('PIN saved');
      setPinEditorOpen(false);
      setCurrentPin('');
      setNewPin('');
      setConfirmPin('');
      void queryClient.invalidateQueries({ queryKey: ['users-pin-status'] });
      void queryClient.invalidateQueries({ queryKey: ['users-me'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const clearPinMut = useMutation({
    mutationFn: () => deletePin(),
    onSuccess: () => {
      toastSuccess('PIN removed');
      void queryClient.invalidateQueries({ queryKey: ['users-pin-status'] });
      void queryClient.invalidateQueries({ queryKey: ['users-me'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const signOutOthersMut = useMutation({
    mutationFn: () => signOutOtherSessions(),
    onSuccess: (data) => toastSuccess(`Signed out ${data.revoked} other session(s)`),
    onError: (e) => toastError(parseApiError(e).message),
  });

  // ── Notifications ─────────────────────────────────────────────────────────
  const [notifPrefs, setNotifPrefs] = useState<NotificationPrefs | null>(null);
  useEffect(() => {
    if (profile?.notificationPrefs) setNotifPrefs(profile.notificationPrefs);
  }, [profile?.notificationPrefs]);

  const saveNotifMut = useMutation({
    mutationFn: () => updateMyNotificationPrefs(notifPrefs ?? {}),
    onSuccess: (data) => {
      toastSuccess('Notification preferences saved');
      setNotifPrefs(data);
      void queryClient.invalidateQueries({ queryKey: ['users-me'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  // ── Preferences ───────────────────────────────────────────────────────────
  const [prefs, setPrefs] = useState<UserPreferences | null>(null);
  useEffect(() => {
    if (profile?.preferences) setPrefs(profile.preferences);
  }, [profile?.preferences]);

  const savePrefsMut = useMutation({
    mutationFn: () => updateMyPreferences(prefs ?? {}),
    onSuccess: (data) => {
      toastSuccess('Preferences saved');
      setPrefs(data);
      applyPreferencesLocally(data);
      if (data.theme) void setTheme(data.theme);
      void queryClient.invalidateQueries({ queryKey: ['users-me'] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function selectTab(id: TabId) {
    setTab(id);
    const hash = TABS.find((t) => t.id === id)?.hash;
    navigate(hash ? `/settings/profile#${hash}` : '/settings/profile', { replace: true });
  }

  if (profileQ.isLoading || !profile) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        Loading profile…
      </div>
    );
  }

  if (profileQ.isError) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-red-800">
        Could not load your profile.
      </div>
    );
  }

  const displayName = `${profile.firstName} ${profile.lastName}`.trim();
  const initials = initialsFromName(profile.firstName, profile.lastName);
  const avatarColor = avatarColorFromName(displayName);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-slate-950 dark:text-white">My Profile</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Manage your account, security, and preferences.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2 dark:border-slate-700">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => selectTab(t.id)}
            className={cn(
              'rounded-xl px-3 py-2 text-sm font-semibold transition',
              tab === t.id
                ? 'bg-primary text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'profile' ? (
        <div className="space-y-6">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <div className="flex flex-wrap items-center gap-4">
              {profile.avatarUrl ? (
                <img
                  src={profile.avatarUrl}
                  alt=""
                  className="h-20 w-20 rounded-full object-cover ring-2 ring-slate-200"
                />
              ) : (
                <div
                  className="flex h-20 w-20 items-center justify-center rounded-full text-2xl font-bold text-white"
                  style={{ backgroundColor: avatarColor }}
                >
                  {initials}
                </div>
              )}
              <div>
                <p className="font-semibold text-slate-950 dark:text-white">{displayName}</p>
                <p className="text-sm text-slate-500">{profile.email}</p>
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300"
                    onClick={() => toastSuccess('Photo upload coming soon')}
                  >
                    {profile.avatarUrl ? 'Change Photo' : 'Upload Photo'}
                  </button>
                  {profile.avatarUrl ? (
                    <button
                      type="button"
                      className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
                      onClick={() => toastSuccess('Photo remove coming soon')}
                    >
                      Remove Photo
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-950 dark:text-white">Personal info</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="font-medium text-slate-700 dark:text-slate-300">First name *</span>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </label>
              <label className="block text-sm">
                <span className="font-medium text-slate-700 dark:text-slate-300">Last name *</span>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="font-medium text-slate-700 dark:text-slate-300">Email *</span>
                <input
                  type="email"
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <label className="block text-sm sm:col-span-2">
                <span className="font-medium text-slate-700 dark:text-slate-300">Phone</span>
                <input
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Used for SMS notifications"
                />
              </label>
            </div>
            <button
              type="button"
              disabled={saveProfileMut.isPending || !firstName.trim() || !lastName.trim() || !email.trim()}
              onClick={() => saveProfileMut.mutate()}
              className="mt-4 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-[#164477] disabled:opacity-50"
            >
              {saveProfileMut.isPending ? 'Saving…' : 'Save Profile'}
            </button>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-950 dark:text-white">Your role</h3>
            <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-slate-500">Role</dt>
                <dd className="font-semibold capitalize text-slate-900 dark:text-white">
                  {prettyLabel(profile.role)}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Store</dt>
                <dd className="font-semibold text-slate-900 dark:text-white">
                  {profile.storeName ?? '—'}
                </dd>
              </div>
              <div>
                <dt className="text-slate-500">Member since</dt>
                <dd className="font-semibold text-slate-900 dark:text-white">
                  {formatDate(profile.createdAt)}
                </dd>
              </div>
            </dl>
          </section>
        </div>
      ) : null}

      {tab === 'security' ? (
        <div className="space-y-6">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-950 dark:text-white">Change password</h3>
            <div className="mt-4 grid max-w-md gap-3">
              <input
                type="password"
                placeholder="Current password"
                className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <input
                type="password"
                placeholder="New password"
                className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <input
                type="password"
                placeholder="Confirm new password"
                className="rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <ul className="space-y-1 text-xs">
                {[
                  [pwChecks.length, 'At least 8 characters'],
                  [pwChecks.number, 'At least one number'],
                  [pwChecks.upper, 'At least one uppercase letter'],
                ].map(([ok, label]) => (
                  <li
                    key={String(label)}
                    className={ok ? 'text-emerald-600' : 'text-slate-400'}
                  >
                    {ok ? '✓' : '○'} {label}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                disabled={
                  changePwMut.isPending ||
                  !currentPassword ||
                  !pwChecks.length ||
                  !pwChecks.number ||
                  !pwChecks.upper ||
                  newPassword !== confirmPassword
                }
                onClick={() => changePwMut.mutate()}
                className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {changePwMut.isPending ? 'Changing…' : 'Change Password'}
              </button>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-950 dark:text-white">🔢 Your PIN</h3>
            {pinQ.data?.hasPinSet ? (
              <div className="mt-3 space-y-3 text-sm text-slate-600 dark:text-slate-300">
                <p>
                  Status: <span className="font-semibold text-emerald-600">✓ PIN set</span>
                </p>
                {pinQ.data.pinSetAt ? (
                  <p>Set on: {formatDate(pinQ.data.pinSetAt)}</p>
                ) : null}
                <p>
                  Your PIN is required for certain actions like taking payments and applying
                  discounts.
                </p>
                {!pinEditorOpen ? (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                      onClick={() => setPinEditorOpen(true)}
                    >
                      Change PIN
                    </button>
                    <button
                      type="button"
                      className="rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
                      onClick={() => {
                        if (window.confirm('Remove your PIN?')) clearPinMut.mutate();
                      }}
                    >
                      Remove PIN
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="mt-3 space-y-3 text-sm text-slate-600 dark:text-slate-300">
                <p className="font-semibold text-amber-700">⚠ No PIN set</p>
                <p>
                  Some actions require a PIN. Set yours now to avoid being locked out during
                  transactions.
                </p>
                {!pinEditorOpen ? (
                  <button
                    type="button"
                    className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
                    onClick={() => setPinEditorOpen(true)}
                  >
                    Set PIN Now
                  </button>
                ) : null}
              </div>
            )}

            {pinEditorOpen ? (
              <div className="mt-4 max-w-xs space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800">
                {pinQ.data?.hasPinSet ? (
                  <label className="block text-sm">
                    <span className="font-medium">Current PIN</span>
                    <input
                      type="password"
                      inputMode="numeric"
                      maxLength={6}
                      className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 font-mono tracking-widest dark:border-slate-600 dark:bg-slate-900"
                      value={currentPin}
                      onChange={(e) => setCurrentPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    />
                  </label>
                ) : null}
                <label className="block text-sm">
                  <span className="font-medium">New PIN (4–6 digits)</span>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 font-mono tracking-widest dark:border-slate-600 dark:bg-slate-900"
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                </label>
                <label className="block text-sm">
                  <span className="font-medium">Confirm PIN</span>
                  <input
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 font-mono tracking-widest dark:border-slate-600 dark:bg-slate-900"
                    value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  />
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={
                      setPinMut.isPending ||
                      !/^\d{4,6}$/.test(newPin) ||
                      newPin !== confirmPin ||
                      (Boolean(pinQ.data?.hasPinSet) && !/^\d{4,6}$/.test(currentPin))
                    }
                    onClick={() => setPinMut.mutate()}
                    className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    Save PIN
                  </button>
                  <button
                    type="button"
                    className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
                    onClick={() => {
                      setPinEditorOpen(false);
                      setCurrentPin('');
                      setNewPin('');
                      setConfirmPin('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-950 dark:text-white">Active sessions</h3>
            <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-4 text-sm dark:border-slate-700 dark:bg-slate-800">
              <p className="font-semibold text-slate-900 dark:text-white">This device (current)</p>
              <p className="text-slate-500">
                {navigator.userAgent.includes('Windows')
                  ? 'Windows'
                  : navigator.userAgent.includes('Mac')
                    ? 'macOS'
                    : 'This browser'}{' '}
                · {profile.storeName ?? 'Current store'}
              </p>
              <p className="text-xs text-slate-400">Last active: Now</p>
            </div>
            <button
              type="button"
              disabled={signOutOthersMut.isPending}
              onClick={() => signOutOthersMut.mutate()}
              className="mt-3 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200"
            >
              Sign out all other devices
            </button>
          </section>
        </div>
      ) : null}

      {tab === 'notifications' && notifPrefs ? (
        <div className="space-y-6">
          <PrefGroup
            title="In-app notifications"
            labels={IN_APP_LABELS}
            values={notifPrefs.inApp}
            onChange={(key, checked) =>
              setNotifPrefs((p) =>
                p ? { ...p, inApp: { ...p.inApp, [key]: checked } } : p,
              )
            }
          />
          <PrefGroup
            title="Email notifications"
            labels={EMAIL_LABELS}
            values={notifPrefs.email}
            onChange={(key, checked) =>
              setNotifPrefs((p) =>
                p ? { ...p, email: { ...p.email, [key]: checked } } : p,
              )
            }
          />
          <label className="block max-w-md text-sm">
            <span className="font-medium text-slate-700 dark:text-slate-300">Email address</span>
            <input
              type="email"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
              value={notifPrefs.emailOverride ?? profile.email}
              onChange={(e) =>
                setNotifPrefs((p) =>
                  p
                    ? {
                        ...p,
                        emailOverride:
                          e.target.value === profile.email ? null : e.target.value || null,
                      }
                    : p,
                )
              }
            />
          </label>
          <PrefGroup
            title="SMS notifications"
            labels={SMS_LABELS}
            values={notifPrefs.sms}
            onChange={(key, checked) =>
              setNotifPrefs((p) => (p ? { ...p, sms: { ...p.sms, [key]: checked } } : p))
            }
          />
          <label className="block max-w-md text-sm">
            <span className="font-medium text-slate-700 dark:text-slate-300">SMS phone</span>
            <input
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
              value={notifPrefs.smsOverride ?? profile.phone ?? ''}
              onChange={(e) =>
                setNotifPrefs((p) =>
                  p ? { ...p, smsOverride: e.target.value || null } : p,
                )
              }
              placeholder="Uses profile phone if blank"
            />
          </label>
          <button
            type="button"
            disabled={saveNotifMut.isPending}
            onClick={() => saveNotifMut.mutate()}
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saveNotifMut.isPending ? 'Saving…' : 'Save Notification Preferences'}
          </button>
        </div>
      ) : null}

      {tab === 'preferences' && prefs ? (
        <div className="space-y-6">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-950 dark:text-white">Display</h3>
            <div className="mt-3 flex flex-wrap gap-3">
              {(
                [
                  ['light', 'Light Mode'],
                  ['dark', 'Dark Mode'],
                  ['system', 'Follow System'],
                ] as const
              ).map(([value, label]) => (
                <label
                  key={value}
                  className={cn(
                    'flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm',
                    prefs.theme === value
                      ? 'border-primary bg-primary/5 font-semibold'
                      : 'border-slate-200 dark:border-slate-600',
                  )}
                >
                  <input
                    type="radio"
                    name="theme"
                    checked={prefs.theme === value}
                    onChange={() => {
                      setPrefs((p) => (p ? { ...p, theme: value } : p));
                      void setTheme(value);
                    }}
                  />
                  {label}
                </label>
              ))}
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-950 dark:text-white">Default views</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <SelectField
                label="Default ticket view"
                value={prefs.defaultTicketView}
                options={[
                  ['list', 'List'],
                  ['cards', 'Cards'],
                  ['kanban', 'Kanban'],
                ]}
                onChange={(v) =>
                  setPrefs((p) =>
                    p ? { ...p, defaultTicketView: v as UserPreferences['defaultTicketView'] } : p,
                  )
                }
              />
              <SelectField
                label="Default ticket queue filter"
                value={prefs.defaultQueueFilter}
                options={[
                  ['all_active', 'All Active'],
                  ['my_tickets', 'My Tickets'],
                  ['unassigned', 'Unassigned'],
                ]}
                onChange={(v) =>
                  setPrefs((p) =>
                    p
                      ? { ...p, defaultQueueFilter: v as UserPreferences['defaultQueueFilter'] }
                      : p,
                  )
                }
              />
              <SelectField
                label="Default report period"
                value={prefs.defaultReportPeriod}
                options={[
                  ['today', 'Today'],
                  ['week', 'This Week'],
                  ['month', 'This Month'],
                  ['quarter', 'This Quarter'],
                  ['year', 'This Year'],
                ]}
                onChange={(v) =>
                  setPrefs((p) =>
                    p
                      ? { ...p, defaultReportPeriod: v as UserPreferences['defaultReportPeriod'] }
                      : p,
                  )
                }
              />
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-950 dark:text-white">Quick ticket defaults</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <SelectField
                label="Default intake type"
                value={prefs.defaultIntakeType}
                options={[
                  ['walk_in', 'Walk-in'],
                  ['drop_off', 'Drop-off'],
                  ['mail_in', 'Mail-in'],
                ]}
                onChange={(v) =>
                  setPrefs((p) =>
                    p ? { ...p, defaultIntakeType: v as UserPreferences['defaultIntakeType'] } : p,
                  )
                }
              />
              <SelectField
                label="Default promised time offset"
                value={prefs.promisedTimeOffset}
                options={[
                  ['same_day', 'Same day'],
                  ['next_day', 'Next day'],
                  ['calculator', 'Use calculator'],
                ]}
                onChange={(v) =>
                  setPrefs((p) =>
                    p
                      ? { ...p, promisedTimeOffset: v as UserPreferences['promisedTimeOffset'] }
                      : p,
                  )
                }
              />
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input
                  type="checkbox"
                  checked={prefs.autoAssignToMe}
                  onChange={(e) =>
                    setPrefs((p) => (p ? { ...p, autoAssignToMe: e.target.checked } : p))
                  }
                />
                Auto-assign tickets to me
              </label>
            </div>
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <h3 className="text-sm font-bold text-slate-950 dark:text-white">Language</h3>
            <SelectField
              label="Display language"
              value={prefs.language}
              options={[['en', 'English']]}
              onChange={(v) => setPrefs((p) => (p ? { ...p, language: v } : p))}
            />
          </section>

          <button
            type="button"
            disabled={savePrefsMut.isPending}
            onClick={() => savePrefsMut.mutate()}
            className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {savePrefsMut.isPending ? 'Saving…' : 'Save Preferences'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function PrefGroup({
  title,
  labels,
  values,
  onChange,
}: {
  title: string;
  labels: Record<string, string>;
  values: Record<string, boolean>;
  onChange: (key: string, checked: boolean) => void;
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <h3 className="text-sm font-bold text-slate-950 dark:text-white">{title}</h3>
      <ul className="mt-3 space-y-2">
        {Object.entries(labels).map(([key, label]) => (
          <li key={key}>
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={Boolean(values[key])}
                onChange={(e) => onChange(key, e.target.checked)}
              />
              {label}
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<[string, string]>;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-slate-700 dark:text-slate-300">{label}</span>
      <select
        className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800 dark:text-white"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  );
}
